import React, { createContext, useContext, useMemo, useState } from "react";
import type { Row } from "@/lib/cleaning-engine";
import { getColumns } from "@/lib/cleaning-engine";

/**
 * Shared working dataset for the Data Cleaning workspace.
 *
 * Every panel on the page (tools, AI cleaner, enrichment, apply/revert)
 * reads and writes the SAME rows, so the "Apply changes" button acts on what
 * the user is actually looking at instead of a private copy.
 */
export interface ActiveDataset {
  key: string;
  label: string;
  rows: Row[];
  columns: string[];
  /** The last saved version number in `dataset_versions`, if any. */
  savedVersion: number | null;
  dirty: boolean;
  pendingChanges: { action: string; rows_affected: number }[];
}

interface Ctx {
  dataset: ActiveDataset | null;
  setDataset: (label: string, rows: Row[], key?: string) => void;
  updateRows: (
    rows: Row[],
    changes?: { action: string; rows_affected: number }[],
  ) => void;
  markSaved: (version: number) => void;
  clear: () => void;
}

const ActiveDatasetContext = createContext<Ctx | null>(null);

const slug = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "dataset";

export const ActiveDatasetProvider = ({ children }: { children: React.ReactNode }) => {
  const [dataset, setState] = useState<ActiveDataset | null>(null);

  const value = useMemo<Ctx>(
    () => ({
      dataset,
      setDataset: (label, rows, key) =>
        setState({
          key: key ?? slug(label),
          label,
          rows,
          columns: getColumns(rows),
          savedVersion: null,
          dirty: false,
          pendingChanges: [],
        }),
      updateRows: (rows, changes = []) =>
        setState((prev) =>
          prev
            ? {
                ...prev,
                rows,
                columns: getColumns(rows),
                dirty: true,
                pendingChanges: [...prev.pendingChanges, ...changes],
              }
            : prev,
        ),
      markSaved: (version) =>
        setState((prev) =>
          prev ? { ...prev, savedVersion: version, dirty: false, pendingChanges: [] } : prev,
        ),
      clear: () => setState(null),
    }),
    [dataset],
  );

  return (
    <ActiveDatasetContext.Provider value={value}>{children}</ActiveDatasetContext.Provider>
  );
};

export const useActiveDataset = () => {
  const ctx = useContext(ActiveDatasetContext);
  if (!ctx) {
    throw new Error("useActiveDataset must be used inside <ActiveDatasetProvider>");
  }
  return ctx;
};
